import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GalleryThemeProvider } from './context/GalleryThemeContext';
import { CustomerAuthProvider } from './context/CustomerAuthContext';
import { GalleryCartProvider } from './context/GalleryCartContext';
import { GalleryConfigProvider } from './context/GalleryConfigContext';

// Gallery Pages
import GalleryHomePage from './pages/GalleryHomePage';
import EventListPage from './pages/EventListPage';
import PhotoGalleryPage from './pages/PhotoGalleryPage';
import GalleryCartPage from './pages/GalleryCartPage';
import GalleryCheckoutPage from './pages/GalleryCheckoutPage';
import CustomerLoginPage from './pages/CustomerLoginPage';
import CustomerRegisterPage from './pages/CustomerRegisterPage';
import CustomerAccountPage from './pages/CustomerAccountPage';

// Gallery Components
import GalleryLayout from './components/layout/GalleryLayout';
import CustomerProtectedRoute from './components/auth/CustomerProtectedRoute';
import DomainResolver from './components/config/DomainResolver';
import LoadingScreen from './components/common/LoadingScreen';

interface GalleryAppProps {
  domain?: string;
  subdomain?: string;
  basename?: string;
}

const GalleryApp: React.FC<GalleryAppProps> = ({ domain, subdomain, basename }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize gallery app with domain resolution
    const initializeGallery = async () => {
      try {
        setIsLoading(true);
        setConfigError(null);

        // Domain resolution will be handled by DomainResolver component
        // which loads photographer configuration and branding

        setIsLoading(false);
      } catch {
        setConfigError('Failed to load gallery configuration');
        setIsLoading(false);
      }
    };

    initializeGallery();
  }, [domain, subdomain]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Gallery Not Found</h1>
          <p className="text-gray-600 mb-6">{configError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter basename={basename}>
      <DomainResolver domain={domain} subdomain={subdomain}>
        <GalleryConfigProvider>
          <GalleryThemeProvider>
            <CustomerAuthProvider>
              <GalleryCartProvider>
                <GalleryLayout>
                  <Routes>
                    {/* Public Gallery Routes */}
                    <Route path="/" element={<GalleryHomePage />} />
                    <Route path="/events" element={<EventListPage />} />
                    <Route path="/events/:eventId" element={<PhotoGalleryPage />} />
                    <Route path="/cart" element={<GalleryCartPage />} />

                    {/* Customer Authentication */}
                    <Route path="/login" element={<CustomerLoginPage />} />
                    <Route path="/register" element={<CustomerRegisterPage />} />

                    {/* Protected Customer Routes */}
                    <Route
                      path="/checkout"
                      element={
                        <CustomerProtectedRoute>
                          <GalleryCheckoutPage />
                        </CustomerProtectedRoute>
                      }
                    />
                    <Route
                      path="/customers/profile"
                      element={
                        <CustomerProtectedRoute>
                          <CustomerAccountPage />
                        </CustomerProtectedRoute>
                      }
                    />
                    <Route
                      path="/account"
                      element={
                        <CustomerProtectedRoute>
                          <CustomerAccountPage />
                        </CustomerProtectedRoute>
                      }
                    />

                    {/* Catch all route - redirect to gallery home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </GalleryLayout>
              </GalleryCartProvider>
            </CustomerAuthProvider>
          </GalleryThemeProvider>
        </GalleryConfigProvider>
      </DomainResolver>
    </BrowserRouter>
  );
};

export default GalleryApp;
